import { useMemo } from 'react';

import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { scopeDpV2SuggestionsToFirstDeathClient } from '@/modules/aiDP/aidp.utils';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { useEntitySuggestionsContext } from '@/modules/aiSuggestions/context/EntitySuggestions.context';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useBeneficiaryDesignationSuggestions } from '@/modules/beneficiaryDesignations/hooks/useBeneficiaryDesignationSuggestions';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { getAISuggestionsForEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { EntityType } from '@/modules/entities/types/EntityType';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { pluralize } from '@/utils/formatting/strings';

interface AISuggestionsCountBadgeProps {
  entityType: EntityType | null | undefined;
  section: EditEntitySection;
}

/**
 * Displays the count of unacknowledged suggestions for a given section of an entity.
 *
 * @param entityType
 * @param section
 */
export function AISuggestionsCountBadge({
  entityType,
  section,
}: AISuggestionsCountBadgeProps) {
  const { suggestionsByKind } = useEntitySuggestionsContext();
  const { suggestionsByKind: dpSuggestionsByKind } =
    useDispositiveProvisionSuggestionsContext();
  const canEdit = useCanEditHouseholdData();
  const { primaryClients } = useHouseholdDetailsContext();
  const { entityId } = useEntityDetailsContext();

  // Beneficiary-designation suggestions are tier-scoped and fetched by their
  // own query rather than through the entity/DP suggestion contexts above, so
  // they are counted separately. The hook skips its query when passed a null
  // entityId, i.e. for every section other than BENEFICIARY_DESIGNATIONS.
  const { primary, contingent } = useBeneficiaryDesignationSuggestions({
    entityId:
      section === EditEntitySection.BENEFICIARY_DESIGNATIONS ? entityId : null,
  });
  // A skipped query retains its last result, so also gate the count on the
  // section in case this instance previously rendered the BDG section.
  const bdgSuggestionCount =
    section === EditEntitySection.BENEFICIARY_DESIGNATIONS
      ? primary.length + contingent.length
      : 0;

  const unacknowledgedCount = useMemo(() => {
    // Scope DPv2 suggestions to the primary death order so the consolidated
    // dispositions count reflects only the visible death-order scenario.
    // Per-death-order counts are surfaced separately in the death-order
    // selector dropdown.
    const primaryDeathOrderClientId = primaryClients?.[0]?.id;
    const scopedDpSuggestionsByKind = primaryDeathOrderClientId
      ? scopeDpV2SuggestionsToFirstDeathClient(
          dpSuggestionsByKind,
          primaryDeathOrderClientId
        )
      : dpSuggestionsByKind;

    return getAISuggestionsForEntitySection(entityType, section, {
      ...suggestionsByKind,
      ...scopedDpSuggestionsByKind,
    }).filter((s) => !s.acceptanceStatus).length;
  }, [
    dpSuggestionsByKind,
    entityType,
    primaryClients,
    section,
    suggestionsByKind,
  ]);

  const totalCount = unacknowledgedCount + bdgSuggestionCount;

  if (!canEdit || !totalCount) {
    return null;
  }

  return (
    <Badge
      variant={BadgeVariants.Success}
      display={pluralize({
        count: totalCount,
        singular: 'suggestion',
      })}
    />
  );
}
